import { api, ApiError, type SessionPayload } from '../api.ts';
import { el, mount, onTap } from '../ui/dom.ts';
import { errorNote, okNote } from '../ui/feedback.ts';
import { navigate, setPageMeta } from '../router.ts';
import { setSession } from '../state.ts';
import { leaveShell } from '../ui/shell.ts';
import { isCar } from '../ui/surface.ts';
import { BRAND, brandMark } from '../ui/brand.ts';
import { t } from '../i18n/index.ts';
import { withContactLink } from '../ui/contact.ts';
import { langSwitch } from '../ui/lang-switch.ts';

// Giriş / kayıt: açık zemin, dar 440 px form, yanında kontrollü görsel.
// Wordmark landing'e döner. Tek yol e-posta + parola; cihaz eşleştirme
// özelliği ürün kararıyla kaldırıldı.

const VISUAL = '/demo/coast.svg';

function paperShell(main: HTMLElement, visualQuote: { title: string; note: string }): HTMLElement {
  const brand = el('button', { class: 'wordmark', type: 'button', 'aria-label': `${BRAND.title} — ${t('nav.home')}` },
    brandMark(24), el('span', {}, BRAND.name));
  onTap(brand, () => navigate('/'));
  return el('div', { class: 'auth' },
    el('div', { class: 'auth-top' }, brand, langSwitch()),
    el('div', { class: 'auth-form-side' }, main),
    el('div', { class: 'auth-visual' },
      el('img', { src: VISUAL, alt: '' }),
      el('div', { class: 'quote' },
        el('h2', {}, visualQuote.title),
        el('p', {}, visualQuote.note),
      ),
    ),
  );
}

export function authView(_params: Record<string, string>, query: URLSearchParams): void {
  leaveShell();
  document.body.classList.add('on-paper');
  const app = document.getElementById('app')!;
  let mode: 'login' | 'signup' = query.get('mode') === 'signup' ? 'signup' : 'login';
  const nextPlan = query.get('plan');
  // Korumadan gelen `next` buraya döner.
  // Yalnız site içi yol kabul edilir: `//alan.adi` gibi bir değer pushState'te
  // SecurityError fırlatıyor ve başarılı giriş "giriş başarısız" diye görünüyordu.
  const rawNext = query.get('next');
  const after = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') && !rawNext.includes('\\') ? rawNext : null;

  const render = () => {
    const email = el('input', { type: 'email', autocomplete: 'email', placeholder: t('auth.emailPlaceholder'), required: true });
    const password = el('input', {
      type: 'password',
      autocomplete: mode === 'login' ? 'current-password' : 'new-password',
      placeholder: mode === 'login' ? t('auth.passwordPlaceholder') : t('auth.passwordNew'),
      required: true,
    });
    const name = el('input', { type: 'text', placeholder: t('auth.namePlaceholder'), autocomplete: 'name' });
    const problem = el('div', {});
    // Aydınlatma, verinin toplandığı anda yapılmalı: yasal sayfa ayrı durduğu
    // sürece kullanıcı onu hiç görmeden hesap açıyordu.
    const accept = el('input', { type: 'checkbox', id: 'kabul' });
    const termsLink = el('button', { class: 'link-inline', type: 'button' }, t('auth.consent.terms'));
    onTap(termsLink, () => navigate('/legal?section=terms'));
    const kvkkLink = el('button', { class: 'link-inline', type: 'button' }, t('auth.consent.kvkk'));
    onTap(kvkkLink, () => navigate('/legal?section=kvkk'));
    const consent = el('label', { class: 'consent', for: 'kabul' },
      accept,
      el('span', {}, termsLink, t('auth.consent.and'), kvkkLink, t('auth.consent.tail')),
    );
    const submit = el('button', { class: 'btn btn-dark btn-block', type: 'submit' },
      mode === 'login' ? t('auth.login') : t('auth.register'));

    const forgotLink = el('button', { class: 'link-inline', type: 'button' }, t('auth.forgotLink'));
    onTap(forgotLink, () => navigate('/forgot'));
    const switchBtn = el('button', { type: 'button' }, mode === 'login' ? t('auth.switchToRegister') : t('auth.switchToLogin'));
    onTap(switchBtn, () => { mode = mode === 'login' ? 'signup' : 'login'; render(); });

    const form = el('form', { class: 'auth-form' },
      el('h1', {}, mode === 'login' ? t('auth.titleLogin') : t('auth.titleRegister')),
      el('p', { class: 'lead' }, mode === 'login' ? t('auth.leadLogin') : t('auth.leadRegister')),
      problem,
      // Ad, parolanın ARKASINDA duruyordu: kullanıcı parolayı yazdıktan sonra
      // kişisel bilgiye geri dönüyordu. Kimlik bilgileri önce.
      mode === 'signup' ? fieldOf(t('auth.name'), name) : null,
      fieldOf(t('auth.email'), email),
      fieldOf(t('auth.password'), password),
      mode === 'signup' ? consent : null,
      submit,
      el('p', { class: 'auth-switch' },
        mode === 'login' ? t('auth.noAccount') : t('auth.haveAccount'), switchBtn),
      // Parolasını unutan kullanıcı Ayarlar'a giremez; sıfırlamanın yolu burada durur.
      mode === 'login' ? el('p', { class: 'auth-forgot' }, forgotLink) : null,
      // Araç ekranında sürüş uyarısı girişte de görünür (yasal metin bunu söylüyor).
      isCar() ? el('p', { class: 'drive-note' }, t('auth.car.drive')) : null,
    );

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (mode === 'signup' && !accept.checked) {
        mount(problem, errorNote(t('auth.consentRequired')));
        return;
      }
      submit.disabled = true;
      mount(problem, '');
      try {
        const payload = await api.post<SessionPayload>(`/api/auth/${mode === 'login' ? 'login' : 'register'}`, {
          email: email.value,
          password: password.value,
          displayName: name.value,
          deviceName: api.deviceLabel(),
          // Güncel Tesla tarayıcısı adından tanınmıyor; sunucu türü buradan öğrenir.
          deviceKind: isCar() ? 'car' : undefined,
          acceptedTerms: mode === 'signup' ? accept.checked : undefined,
        });
        setSession(payload);
        navigate(after ? after : nextPlan ? '/plans' : '/browse');
      } catch (err) {
        mount(problem, errorNote(err instanceof ApiError ? err.message : t('auth.fail')));
      } finally {
        submit.disabled = false;
      }
    });

    mount(app, paperShell(form, {
      title: t('auth.visualTitle'),
      note: t('auth.visualNote'),
    }));
    email.focus();
  };

  render();
}

/** Parola sıfırlama bağlantısı iste. Yanıt, hesabın var olup olmadığını söylemez. */
export function forgotView(): void {
  leaveShell();
  document.body.classList.add('on-paper');
  setPageMeta({ title: t('auth.reset.titleForgot'), index: false });
  const app = document.getElementById('app')!;
  const email = el('input', { type: 'email', autocomplete: 'email', placeholder: t('auth.emailPlaceholder'), required: true });
  const problem = el('div', {});
  const submit = el('button', { class: 'btn btn-dark btn-block', type: 'submit' }, t('auth.reset.send'));
  const back = el('button', { type: 'button' }, t('auth.switchToLogin'));
  onTap(back, () => navigate('/login'));

  const form = el('form', { class: 'auth-form' },
    el('h1', {}, t('auth.reset.titleForgot')),
    el('p', { class: 'lead' }, t('auth.reset.leadForgot')),
    problem,
    fieldOf(t('auth.email'), email),
    submit,
    el('p', { class: 'auth-switch' }, t('auth.reset.remembered'), back),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    mount(problem, '');
    try {
      const result = await api.post<{ notice: string; minutes: number }>('/api/auth/password/forgot', { email: email.value });
      mount(problem, okNote(`${result.notice} ${t('auth.reset.validFor', { n: result.minutes })}`));
    } catch (err) {
      // E-posta gönderimi kapalıysa eski yol geçerlidir: İletişim adresi.
      if (err instanceof ApiError && err.code === 'reset_unavailable') {
        mount(problem, el('p', { class: 'error-note', role: 'alert' }, ...withContactLink('auth.forgot')));
      } else {
        mount(problem, errorNote(err instanceof ApiError ? err.message : t('auth.fail')));
      }
    } finally {
      submit.disabled = false;
    }
  });

  mount(app, paperShell(form, { title: t('auth.visualTitle'), note: t('auth.visualNote') }));
  email.focus();
}

/** E-postadaki bağlantıyla yeni parola belirle. */
export function resetView(_params: Record<string, string>, query: URLSearchParams): void {
  leaveShell();
  document.body.classList.add('on-paper');
  setPageMeta({ title: t('auth.reset.titleNew'), index: false });
  const app = document.getElementById('app')!;
  const token = query.get('token') ?? '';
  // Jeton adres çubuğunda ve tarayıcı geçmişinde kalmasın.
  if (token) history.replaceState(history.state, '', '/reset');

  const requestNew = () => {
    const button = el('button', { class: 'btn btn-dark btn-block', type: 'button' }, t('auth.reset.requestNew'));
    onTap(button, () => navigate('/forgot'));
    return button;
  };
  const shell = (main: HTMLElement) =>
    mount(app, paperShell(main, { title: t('auth.visualTitle'), note: t('auth.visualNote') }));

  if (!token) {
    shell(el('div', { class: 'auth-form' },
      el('h1', {}, t('auth.reset.titleNew')),
      errorNote(t('auth.reset.missing')),
      requestNew(),
    ));
    return;
  }

  const password = el('input', { type: 'password', autocomplete: 'new-password', placeholder: t('auth.passwordNew'), required: true });
  const problem = el('div', {});
  const submit = el('button', { class: 'btn btn-dark btn-block', type: 'submit' }, t('auth.reset.save'));
  const form = el('form', { class: 'auth-form' },
    el('h1', {}, t('auth.reset.titleNew')),
    el('p', { class: 'lead' }, t('auth.reset.leadNew')),
    problem,
    fieldOf(t('auth.reset.newPassword'), password),
    submit,
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    mount(problem, '');
    try {
      const result = await api.post<{ notice: string }>('/api/auth/password/reset', { token, newPassword: password.value });
      // Sunucu bütün oturumları kapattı; bu sekmedeki oturum bilgisi de düşmeli.
      setSession(null);
      const login = el('button', { class: 'btn btn-dark btn-block', type: 'button' }, t('auth.login'));
      onTap(login, () => navigate('/login'));
      shell(el('div', { class: 'auth-form' },
        el('h1', {}, t('auth.reset.titleNew')),
        okNote(result.notice),
        login,
      ));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'reset_invalid') {
        shell(el('div', { class: 'auth-form' },
          el('h1', {}, t('auth.reset.titleNew')),
          errorNote(err.message),
          requestNew(),
        ));
        return;
      }
      mount(problem, errorNote(err instanceof ApiError ? err.message : t('auth.fail')));
      submit.disabled = false;
    }
  });

  shell(form);
  password.focus();
}

function fieldOf(label: string, input: HTMLElement): HTMLElement {
  const id = 'a_' + Math.random().toString(36).slice(2, 9);
  input.id = id;
  return el('div', { class: 'field' }, el('label', { for: id }, label), input);
}
